// src/router/index.js
import { createRouter, createWebHistory } from 'vue-router';
import { langueSelonRoute } from '@/i18n';
import nextRoute from '../services/next-route';
import store from '../store';

import Login from '../views/auth/Login.vue';
import ChangePassword from '../views/auth/ChangePassword.vue';
import AdminDashboard from '../views/admin/Dashboard.vue';
import AdminClients from '../views/admin/Clients.vue';
import AdminClientDetails from '../views/admin/ClientDetails.vue';
import AdminDevices from '../views/admin/Devices.vue';
import AdminDeviceDetails from '../views/admin/DeviceDetails.vue';
import AdminFirmwares from '../views/admin/Firmwares.vue';
import ClientDashboard from '../views/client/Dashboard.vue';
import ClientDeviceDetails from '../views/client/DeviceDetails.vue';

const routes = [
  { path: '/', redirect: '/login' },
  {
    path: '/login',
    name: 'Login',
    component: Login,
    meta: { requiresAuth: false }
  },
  {
    path: '/change-password',
    name: 'ChangePassword',
    component: ChangePassword,
    meta: { requiresAuth: true }
  },
  {
    // Destination du QR imprimé sur le guide de démarrage livré avec le produit. Publique par
    // construction : le QR est sur du papier, entre les mains de n'importe qui.
    path: '/signup',
    name: 'Signup',
    component: () => import('../views/auth/Signup.vue')
  },
  {
    // Lien d'invitation d'un administrateur, cliqué dans un courriel. Publique par nature :
    // l'invité n'a pas encore de compte. Le jeton du chemin EST la preuve.
    path: '/admin-invitation/:token',
    name: 'InvitationAdmin',
    component: () => import('../views/auth/InvitationAdmin.vue'),
    meta: { requiresAuth: false }
  },
  {
    // Invitation d'un MEMBRE par le titulaire d'un compte client (comptes partagés). Publique,
    // comme celle d'un administrateur : le jeton du chemin est la preuve.
    path: '/invitation-membre/:token',
    name: 'InvitationMembre',
    component: () => import('../views/auth/InvitationMembre.vue'),
    meta: { requiresAuth: false }
  },
  {
    // Sécurité du compte : le même écran pour les deux rôles, la mise en page suit.
    path: '/securite',
    name: 'Securite',
    component: () => import('../views/Securite.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/reset-password',
    name: 'ResetPassword',
    component: () => import('../views/auth/ResetPassword.vue'),
    meta: { requiresAuth: false }
  },
  // Admin
  {
    path: '/admin',
    name: 'AdminDashboard',
    component: AdminDashboard,
    meta: { requiresAuth: true, requiresAdmin: true }
  },
  {
    path: '/admin/clients',
    name: 'AdminClients',
    component: AdminClients,
    meta: { requiresAuth: true, requiresAdmin: true }
  },
  {
    path: '/admin/clients/:clientId',
    name: 'AdminClientDetails',
    component: AdminClientDetails,
    meta: { requiresAuth: true, requiresAdmin: true }
  },
  {
    path: '/admin/devices',
    name: 'AdminDevices',
    component: AdminDevices,
    meta: { requiresAuth: true, requiresAdmin: true }
  },
  {
    path: '/admin/devices/:deviceId',
    name: 'AdminDeviceDetails',
    component: AdminDeviceDetails,
    meta: { requiresAuth: true, requiresAdmin: true }
  },
  {
    path: '/admin/events',
    name: 'AdminEvents',
    component: () => import('../views/admin/Events.vue'),
    meta: { requiresAuth: true, requiresAdmin: true }
  },
  {
    path: '/admin/administrateurs',
    name: 'Administrateurs',
    component: () => import('../views/admin/Administrateurs.vue'),
    meta: { requiresAuth: true, requiresAdmin: true }
  },
  {
    // Consommation de données des SIM 1NCE (06/10/2026).
    path: '/admin/donnees',
    name: 'AdminDonneesSim',
    component: () => import('../views/admin/DonneesSim.vue'),
    meta: { requiresAuth: true, requiresAdmin: true }
  },
  {
    path: '/admin/firmware',
    name: 'AdminFirmwares',
    component: AdminFirmwares,
    meta: { requiresAuth: true, requiresAdmin: true }
  },
  // Client
  {
    path: '/client',
    name: 'ClientDashboard',
    component: ClientDashboard,
    meta: { requiresAuth: true, requiresClient: true }
  },
  {
    // Destination des QR de rattachement (courriel et étiquette du colis). Le code arrive dans
    // le FRAGMENT, que le routeur expose en `route.hash` et qui n'atteint jamais le serveur.
    path: '/client/claim',
    name: 'ClaimDevice',
    component: () => import('../views/client/ClaimDevice.vue'),
    meta: { requiresAuth: true, requiresClient: true }
  },
  {
    path: '/client/events',
    name: 'ClientEvents',
    component: () => import('../views/client/Events.vue'),
    meta: { requiresAuth: true, requiresClient: true }
  },
  {
    // Comptes partagés : réservé au titulaire (le serveur refuse aussi la liste aux membres).
    path: '/client/membres',
    name: 'ClientMembres',
    component: () => import('../views/client/Membres.vue'),
    meta: { requiresAuth: true, requiresClient: true }
  },
  {
    // Suppression du compte (B9) : aussi l'adresse web exigée par Google Play.
    path: '/compte/suppression',
    name: 'SuppressionCompte',
    component: () => import('../views/client/SuppressionCompte.vue'),
    meta: { requiresAuth: true, requiresClient: true }
  },
  {
    path: '/client/devices/:deviceId',
    name: 'ClientDeviceDetails',
    component: ClientDeviceDetails,
    meta: { requiresAuth: true, requiresClient: true }
  },
  // 404
  {
    path: '/:pathMatch(.*)*',
    name: 'NotFound',
    component: () => import('../views/NotFound.vue')
  }
];

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes
});

// Multilingue : français imposé sur l'admin, langue choisie par le client ailleurs.
router.beforeEach((to, from, next) => { langueSelonRoute(to.path); next(); });

router.beforeEach((to, from, next) => {
  const requiresAuth   = to.matched.some(r => r.meta.requiresAuth);
  const requiresAdmin  = to.matched.some(r => r.meta.requiresAdmin);
  const requiresClient = to.matched.some(r => r.meta.requiresClient);

  const isAuthenticated = store.getters['auth/isAuthenticated'];
  const isAdmin  = store.getters['auth/isAdmin'];
  const isClient = store.getters['auth/isClient'];

  if (requiresAuth && !isAuthenticated) {
    // La destination est CONSERVÉE, et avec elle le fragment (`to.fullPath` l'inclut). Sans
    // ça, un client qui scanne le QR de rattachement sans être connecté perdait son code en
    // route : il arrivait sur le tableau de bord, sans rien comprendre à ce qui venait de se
    // passer, et devait ressortir son courriel.
    next({ path: '/login', query: { next: to.fullPath } });
  } else if (requiresAdmin && !isAdmin) {
    next('/login');
  } else if (requiresClient && !isClient) {
    next('/login');
  } else if (to.path === '/login' && isAuthenticated) {
    // Déjà connecté et on arrive sur /login avec une destination en attente (QR scanné dans un
    // onglet où la session est vivante) : on y va directement.
    const suite = typeof to.query.next === 'string' ? to.query.next : null;
    next(nextRoute.estInterne(suite) ? suite : (isAdmin ? '/admin' : '/client'));
  } else {
    next();
  }
});

// Après un déploiement, un onglet resté ouvert réclame des morceaux de l'ANCIENNE version
// (`/js/541.<hash>.js`), qui n'existent plus : l'écran visé — Administrateurs, par exemple,
// chargé à la demande — ne s'ouvrait pas, sans aucun message (constaté le 28/09/2026). On
// recharge alors la page sur la destination, une seule fois par destination et par onglet,
// pour ne jamais boucler si le fichier manque vraiment.
router.onError((erreur, destination) => {
  const message = String(erreur && erreur.message || '');
  if (!/Loading (CSS )?chunk|ChunkLoadError|Failed to fetch dynamically imported module/i.test(message)) return;
  const cle = `rechargement:${destination && destination.fullPath}`;
  try {
    if (sessionStorage.getItem(cle)) return;
    sessionStorage.setItem(cle, '1');
  } catch { /* stockage indisponible : on recharge quand même */ }
  window.location.assign(destination ? destination.fullPath : window.location.href);
});

export default router;
